"use client";
/**
 * Next.js 全局兜底：当 RootLayout / Providers 自身渲染失败时被它捕获。
 * 替代默认的「Application error: a client-side exception has occurred」通用页，
 * 把真实 error.message + stack 显示出来 + 一键复制按钮，方便诊断。
 *
 * 必须重新声明 <html><body>，因为它替换的是 RootLayout 整体。
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const copy = async () => {
    const text = [
      `Error: ${error?.message || "unknown"}`,
      `Digest: ${error?.digest || "(none)"}`,
      "",
      "Stack:",
      error?.stack || "(no stack)",
      "",
      `URL: ${typeof location !== "undefined" ? location.href : "?"}`,
      `UA: ${typeof navigator !== "undefined" ? navigator.userAgent : "?"}`,
      `Time: ${new Date().toISOString()}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      alert("错误信息已复制到剪贴板，请粘贴给运维 / 开发同学");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      ta.remove();
      alert("已尝试复制（旧浏览器），请检查剪贴板");
    }
  };

  return (
    <html lang="zh-CN">
      <body style={{ fontFamily: "system-ui, sans-serif", margin: 0 }}>
        <div style={{ padding: 24, maxWidth: 960, margin: "40px auto" }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: "#dc2626" }}>
            ⚠️ 页面崩溃了（全局兜底）
          </h1>
          <p style={{ color: "#6b7280", marginTop: 4 }}>
            把下面的错误信息复制给运维 / 开发同学，可以直接定位问题。
          </p>
          <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={copy}
              style={{ padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", background: "#f9fafb" }}
            >
              📋 复制错误信息
            </button>
            <button
              onClick={() => reset()}
              style={{ padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", background: "#f9fafb" }}
            >
              🔄 重试
            </button>
            <button
              onClick={() => location.reload()}
              style={{ padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", background: "#f9fafb" }}
            >
              ⟳ 刷新页面
            </button>
          </div>
          <pre
            style={{
              marginTop: 16,
              padding: 12,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 6,
              color: "#991b1b",
              fontSize: 13,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: 360,
              overflow: "auto",
            }}
          >
{`Error: ${error?.message || "unknown"}
Digest: ${error?.digest || "(none)"}

Stack:
${error?.stack || "(no stack)"}

URL: ${typeof location !== "undefined" ? location.href : "?"}
UA: ${typeof navigator !== "undefined" ? navigator.userAgent : "?"}`}
          </pre>
          <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 16 }}>
            提示：如果你装了浏览器扩展（翻译 / 划词 / 浮动助手 / AI 助手等），
            它们注入页面 DOM 可能干扰 React 渲染。可以试试**无痕窗口** + **关掉所有扩展**
            打开本页面对比，能复现 → 是页面 bug；不能复现 → 是扩展冲突。
          </p>
        </div>
      </body>
    </html>
  );
}
